import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/api';

interface CleaningTask {
  id: string;
  name: string;
  area: string;
  frequency: string;
  estimated_minutes: number;
  is_active: boolean;
  last_completed?: string;
  is_overdue?: boolean;
}

const AREAS = ['Kitchen', 'Bathroom', 'Bedroom', 'Living Room', 'Other'];
const FREQUENCIES = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Every 2 Weeks' },
  { value: 'monthly', label: 'Monthly' }
];

const CleaningChecklist: React.FC = () => {
  const [tasks, setTasks] = useState<CleaningTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newTask, setNewTask] = useState({ name: '', area: 'Kitchen', frequency: 'weekly', estimatedMinutes: 15 });
  const [filter, setFilter] = useState<string>('all');

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const response = await apiService.getCleaningSummary();
      if (response.success) {
        setTasks(response.tasks || []);
      }
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTask = async () => {
    if (!newTask.name.trim()) return;

    try {
      const response = await apiService.createCleaningTask(newTask);
      if (response.success) {
        await fetchTasks();
        setNewTask({ name: '', area: 'Kitchen', frequency: 'weekly', estimatedMinutes: 15 });
        setShowAddForm(false);
      }
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to create task');
    }
  };

  const handleComplete = async (task: CleaningTask) => {
    try {
      await apiService.completeCleaningTask(task.id);
      await fetchTasks();
    } catch (error) {
      console.error('Failed to complete task:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this cleaning task?')) return;

    try {
      await apiService.deleteCleaningTask(id);
      setTasks(tasks.filter(t => t.id !== id));
    } catch (error) {
      console.error('Failed to delete task:', error);
    }
  };

  const getDaysAgo = (dateStr?: string) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    const today = new Date();
    const diffTime = today.getTime() - date.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getFrequencyColor = (frequency: string) => {
    switch (frequency) {
      case 'daily': return '#ef4444';
      case 'weekly': return '#f59e0b';
      case 'biweekly': return '#3b82f6';
      case 'monthly': return '#8b5cf6';
      default: return '#6b7280';
    }
  };

  const filteredTasks = tasks.filter(task => {
    if (filter === 'all') return true;
    if (filter === 'overdue') return task.is_overdue;
    return task.area === filter;
  });

  const groupedTasks = filteredTasks.reduce((acc, task) => {
    const area = task.area || 'Other';
    if (!acc[area]) acc[area] = [];
    acc[area].push(task);
    return acc;
  }, {} as Record<string, CleaningTask[]>);

  const overdueTasks = tasks.filter(t => t.is_overdue);
  const totalMinutes = tasks.reduce((sum, t) => sum + (t.estimated_minutes || 0), 0);

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{ width: 40, height: 40, border: '4px solid #e5e7eb', borderTop: '4px solid #667eea', borderRadius: '50%', margin: '0 auto', animation: 'spin 1s linear infinite' }}></div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#1f2937' }}>Cleaning Checklist</h2>
          <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: '0.875rem' }}>Keep your home tidy with scheduled tasks</p>
        </div>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          data-testid="add-cleaning-task-btn"
          style={{
            padding: '10px 20px',
            backgroundColor: '#667eea',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontWeight: 600,
            cursor: 'pointer'
          }}
        >
          + Add Task
        </button>
      </div>

      {/* Usage Tips */}
      <div style={{ 
        padding: '16px', 
        backgroundColor: '#eff6ff', 
        borderRadius: '10px', 
        marginBottom: '20px',
        border: '1px solid #93c5fd'
      }}>
        <div style={{ fontSize: '0.875rem', color: '#1e40af', fontWeight: 600, marginBottom: '8px' }}>
          Tips for Cleaning Tasks
        </div>
        <ul style={{ margin: 0, paddingLeft: '20px', color: '#1e3a8a', fontSize: '0.8rem', lineHeight: 1.6 }}>
          <li>Set up rotating chores with daily, weekly, or monthly frequencies</li>
          <li>Red indicators show overdue tasks that need attention</li>
          <li>Click the check button to log completion - tracks your last done date</li>
          <li>Assign tasks to different areas of your home for better organization</li>
          <li>Default tasks are provided - customize them to match your home</li>
        </ul>
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: '16px', marginBottom: '24px' }}>
        <div style={{ padding: '16px 24px', backgroundColor: overdueTasks.length > 0 ? '#fef2f2' : '#f0fdf4', borderRadius: '12px', flex: 1 }}>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: overdueTasks.length > 0 ? '#dc2626' : '#16a34a' }}>
            {overdueTasks.length}
          </div>
          <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Overdue Tasks</div>
        </div>
        <div style={{ padding: '16px 24px', backgroundColor: 'white', borderRadius: '12px', flex: 1 }}>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: '#667eea' }}>{tasks.length}</div>
          <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Total Tasks</div>
        </div>
        <div style={{ padding: '16px 24px', backgroundColor: 'white', borderRadius: '12px', flex: 1 }}>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: '#f59e0b' }}>{totalMinutes}</div>
          <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Est. Minutes/Week</div>
        </div>
      </div>

      {/* Add Form */}
      {showAddForm && (
        <div style={{ padding: '20px', backgroundColor: 'white', borderRadius: '12px', marginBottom: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '1rem', fontWeight: 600 }}>Add New Task</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '12px', alignItems: 'end' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, marginBottom: '4px' }}>Task Name</label>
              <input
                type="text"
                value={newTask.name}
                onChange={(e) => setNewTask({ ...newTask, name: e.target.value })}
                placeholder="e.g., Vacuum living room"
                style={{ width: '100%', padding: '10px', border: '1px solid #e5e7eb', borderRadius: '8px' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, marginBottom: '4px' }}>Area</label>
              <select
                value={newTask.area}
                onChange={(e) => setNewTask({ ...newTask, area: e.target.value })}
                style={{ width: '100%', padding: '10px', border: '1px solid #e5e7eb', borderRadius: '8px' }}
              >
                {AREAS.map(area => <option key={area} value={area}>{area}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, marginBottom: '4px' }}>Frequency</label>
              <select
                value={newTask.frequency}
                onChange={(e) => setNewTask({ ...newTask, frequency: e.target.value })}
                style={{ width: '100%', padding: '10px', border: '1px solid #e5e7eb', borderRadius: '8px' }}
              >
                {FREQUENCIES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, marginBottom: '4px' }}>Est. Minutes</label>
              <input
                type="number"
                value={newTask.estimatedMinutes}
                onChange={(e) => setNewTask({ ...newTask, estimatedMinutes: parseInt(e.target.value) || 15 })}
                style={{ width: '100%', padding: '10px', border: '1px solid #e5e7eb', borderRadius: '8px' }}
              />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
            <button onClick={handleAddTask} style={{ padding: '10px 20px', backgroundColor: '#10b981', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}>
              Create Task
            </button>
            <button onClick={() => setShowAddForm(false)} style={{ padding: '10px 20px', backgroundColor: '#e5e7eb', color: '#374151', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Filter */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <button
          onClick={() => setFilter('all')}
          style={{
            padding: '8px 16px',
            backgroundColor: filter === 'all' ? '#667eea' : 'white',
            color: filter === 'all' ? 'white' : '#374151',
            border: '1px solid #e5e7eb',
            borderRadius: '20px',
            cursor: 'pointer',
            fontWeight: 500
          }}
        >
          All
        </button>
        <button
          onClick={() => setFilter('overdue')}
          style={{
            padding: '8px 16px',
            backgroundColor: filter === 'overdue' ? '#ef4444' : 'white',
            color: filter === 'overdue' ? 'white' : '#374151',
            border: '1px solid #e5e7eb',
            borderRadius: '20px',
            cursor: 'pointer',
            fontWeight: 500
          }}
        >
          Overdue ({overdueTasks.length})
        </button>
        {AREAS.map(area => (
          <button
            key={area}
            onClick={() => setFilter(area)}
            style={{
              padding: '8px 16px',
              backgroundColor: filter === area ? '#667eea' : 'white',
              color: filter === area ? 'white' : '#374151',
              border: '1px solid #e5e7eb',
              borderRadius: '20px',
              cursor: 'pointer',
              fontWeight: 500
            }}
          >
            {area}
          </button>
        ))}
      </div>

      {/* Tasks by Area */}
      {Object.entries(groupedTasks).map(([area, areaTasks]) => (
        <div key={area} style={{ marginBottom: '24px' }}>
          <h3 style={{ margin: '0 0 12px 0', fontSize: '1rem', fontWeight: 600, color: '#374151' }}>
            {area}
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {areaTasks.map(task => {
              const daysAgo = getDaysAgo(task.last_completed);
              return (
                <div
                  key={task.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '16px',
                    backgroundColor: task.is_overdue ? '#fef2f2' : 'white',
                    borderRadius: '10px',
                    border: task.is_overdue ? '2px solid #fecaca' : '1px solid #e5e7eb'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <button
                      onClick={() => handleComplete(task)}
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        border: '2px solid #10b981',
                        backgroundColor: 'white',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#10b981',
                        fontWeight: 700
                      }}
                    >
                      check
                    </button>
                    <div>
                      <div style={{ fontWeight: 600, color: '#1f2937' }}>{task.name}</div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280', display: 'flex', gap: '12px', marginTop: '4px' }}>
                        <span style={{ 
                          padding: '2px 8px', 
                          backgroundColor: `${getFrequencyColor(task.frequency)}20`,
                          color: getFrequencyColor(task.frequency),
                          borderRadius: '10px',
                          fontWeight: 600
                        }}>
                          {FREQUENCIES.find(f => f.value === task.frequency)?.label}
                        </span>
                        <span>{task.estimated_minutes} min</span>
                        {daysAgo !== null && (
                          <span style={{ color: task.is_overdue ? '#dc2626' : '#6b7280' }}>
                            Last done: {daysAgo === 0 ? 'Today' : `${daysAgo} days ago`}
                          </span>
                        )}
                        {daysAgo === null && <span style={{ color: '#9ca3af' }}>Never done</span>}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDelete(task.id)}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: '#fee2e2',
                      color: '#dc2626',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '0.75rem'
                    }}
                  >
                    Delete
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {filteredTasks.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
          {filter === 'all' ? 'No cleaning tasks yet. Add your first task!' : `No tasks in this category.`}
        </div>
      )}
    </div>
  );
};

export default CleaningChecklist;
